import { useState, useEffect, useRef } from 'react';
import { getProducts, createProduct, updateProduct, deleteProduct, getStockSummary, getStockAlerts, getStockMovements, createStockMovement, importStockCsv, getShopifyProducts, importShopifyProducts, syncShopifyStock, getStockProfitability } from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Pencil, Trash2, Package, Loader2, AlertTriangle, ArrowDownCircle, ArrowUpCircle, Upload, Boxes, Wrench, TrendingUp, BarChart3, History, ShoppingBag, RefreshCw, Link2, Check, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { toast } from 'sonner';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stockSummary, setStockSummary] = useState(null);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [movementProduct, setMovementProduct] = useState(null);
  const [movementData, setMovementData] = useState({ type: 'in', quantity: 1, reason: '' });
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [movements, setMovements] = useState([]);
  const [historyProduct, setHistoryProduct] = useState(null);
  const fileInputRef = useRef(null);
  const [tab, setTab] = useState('products'); // products, stock, movements
  const [shopifyDialogOpen, setShopifyDialogOpen] = useState(false);
  const [shopifyProducts, setShopifyProducts] = useState([]);
  const [shopifyLoading, setShopifyLoading] = useState(false);
  const [selectedShopifyIds, setSelectedShopifyIds] = useState([]);
  const [shopifyImporting, setShopifyImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [profitData, setProfitData] = useState([]);

  const defaultForm = {
    name: '', description: '', unit_price: 0, purchase_price: 0, vat_rate: 7.7,
    unit: 'unite', product_type: 'service', sku: '', quantity_in_stock: 0, stock_alert_threshold: 5
  };
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [prodRes, summaryRes, alertsRes, profitRes] = await Promise.all([
        getProducts(), getStockSummary(), getStockAlerts(), getStockProfitability().catch(() => ({ data: { products: [] } }))
      ]);
      setProducts(prodRes.data);
      setStockSummary(summaryRes.data);
      setStockAlerts(alertsRes.data);
      setProfitData(profitRes.data.products || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const resetForm = () => { setFormData(defaultForm); setEditingProduct(null); };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name, description: product.description || '', unit_price: product.unit_price,
      purchase_price: product.purchase_price || 0, vat_rate: product.vat_rate, unit: product.unit,
      product_type: product.product_type || 'service', sku: product.sku || '',
      quantity_in_stock: product.quantity_in_stock || 0, stock_alert_threshold: product.stock_alert_threshold || 5
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, formData);
        toast.success('Produit mis a jour');
      } else {
        await createProduct(formData);
        toast.success('Produit cree');
      }
      setDialogOpen(false);
      resetForm();
      loadAll();
    } catch (err) { toast.error('Erreur'); } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await deleteProduct(productToDelete);
      toast.success('Supprime');
      setDeleteDialogOpen(false);
      loadAll();
    } catch (err) { toast.error('Erreur'); }
  };

  const handleStockMovement = async () => {
    if (!movementProduct || movementData.quantity <= 0) return;
    try {
      await createStockMovement({ product_id: movementProduct.id, ...movementData });
      toast.success(movementData.type === 'in' ? 'Entree de stock enregistree' : 'Sortie de stock enregistree');
      setMovementDialogOpen(false);
      setMovementData({ type: 'in', quantity: 1, reason: '' });
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const openHistory = async (product) => {
    setHistoryProduct(product);
    try {
      const res = await getStockMovements(product.id);
      setMovements(res.data);
    } catch (err) { console.error(err); }
    setHistoryDialogOpen(true);
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const res = await importStockCsv(file);
      toast.success(`${res.data.imported} produit(s) importe(s)`);
      if (res.data.errors.length > 0) toast.error(`${res.data.errors.length} erreur(s)`);
      loadAll();
    } catch (err) { toast.error('Erreur import CSV'); }
    e.target.value = '';
  };

  const openShopifyImport = async () => {
    setShopifyDialogOpen(true);
    setShopifyLoading(true);
    setSelectedShopifyIds([]);
    try {
      const res = await getShopifyProducts();
      setShopifyProducts(res.data.products || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur connexion Shopify');
      setShopifyProducts([]);
    } finally { setShopifyLoading(false); }
  };

  const handleShopifyImport = async () => {
    if (selectedShopifyIds.length === 0) { toast.error('Selectionnez au moins un produit'); return; }
    setShopifyImporting(true);
    try {
      const res = await importShopifyProducts(selectedShopifyIds);
      toast.success(`${res.data.imported} produit(s) importe(s) depuis Shopify`);
      if (res.data.errors > 0) toast.error(`${res.data.errors} erreur(s)`);
      setShopifyDialogOpen(false);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur import'); }
    finally { setShopifyImporting(false); }
  };

  const handleSyncShopifyStock = async () => {
    setSyncing(true);
    try {
      const res = await syncShopifyStock();
      if (res.data.synced > 0) {
        toast.success(`${res.data.synced} produit(s) mis a jour depuis Shopify`);
      } else {
        toast.success('Stock deja a jour');
      }
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur sync'); }
    finally { setSyncing(false); }
  };

  const toggleShopifySelect = (id) => {
    setSelectedShopifyIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('fr-CH', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount) + ' CHF';

  const filtered = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) || (p.sku || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || p.product_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const physicalProducts = filtered.filter(p => p.product_type === 'product');
  const services = filtered.filter(p => p.product_type === 'service');

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Produits & Services</h1>
          <p className="text-slate-500 mt-1">Gerez votre catalogue et votre inventaire</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="file" ref={fileInputRef} accept=".csv" onChange={handleCsvImport} className="hidden" />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="import-csv-btn">
            <Upload className="w-4 h-4 mr-2" />CSV
          </Button>
          <Button variant="outline" onClick={openShopifyImport} data-testid="import-shopify-btn" className="text-green-700 border-green-300 hover:bg-green-50">
            <ShoppingBag className="w-4 h-4 mr-2" />Shopify
          </Button>
          {products.some(p => p.shopify_product_id) && (
            <Button variant="outline" onClick={handleSyncShopifyStock} disabled={syncing} data-testid="sync-shopify-btn" className="text-blue-700 border-blue-300 hover:bg-blue-50">
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />{syncing ? 'Sync...' : 'Sync Stock'}
            </Button>
          )}
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="new-product-btn">
            <Plus className="w-4 h-4 mr-2" />Nouveau
          </Button>
        </div>
      </div>

      {/* Stock Summary Cards */}
      {stockSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Boxes className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-slate-500">Produits</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stockSummary.total_products}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-4 h-4 text-emerald-500" />
                <span className="text-xs text-slate-500">En stock</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stockSummary.total_items_in_stock}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-4 h-4 text-indigo-500" />
                <span className="text-xs text-slate-500">Valeur stock</span>
              </div>
              <p className="text-xl font-bold text-slate-900">{formatCurrency(stockSummary.total_stock_value)}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <ArrowDownCircle className="w-4 h-4 text-red-500" />
                <span className="text-xs text-slate-500">Total achats</span>
              </div>
              <p className="text-xl font-bold text-red-600">{formatCurrency(stockSummary.total_purchases || 0)}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <ArrowUpCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-xs text-slate-500">Total ventes</span>
              </div>
              <p className="text-xl font-bold text-emerald-600">{formatCurrency(stockSummary.total_product_sales || 0)}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <span className="text-xs text-slate-500">Benefice realise</span>
              </div>
              <p className={`text-xl font-bold ${(stockSummary.realized_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(stockSummary.realized_profit || 0)}</p>
            </CardContent>
          </Card>
          <Card className={`border-slate-200 ${stockSummary.alerts_count > 0 ? 'border-amber-300 bg-amber-50' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className={`w-4 h-4 ${stockSummary.alerts_count > 0 ? 'text-amber-500' : 'text-slate-400'}`} />
                <span className="text-xs text-slate-500">Alertes</span>
              </div>
              <p className={`text-2xl font-bold ${stockSummary.alerts_count > 0 ? 'text-amber-600' : 'text-slate-900'}`}>{stockSummary.alerts_count}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stock Alerts Banner */}
      {stockAlerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span className="font-semibold text-amber-800">Stock bas ({stockAlerts.length} produit{stockAlerts.length > 1 ? 's' : ''})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {stockAlerts.map(a => (
              <span key={a.id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                {a.name} — {a.quantity_in_stock} restant{a.quantity_in_stock !== 1 ? 's' : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Profitability Chart */}
      {profitData.length > 0 && (
        <Card className="border-slate-200" data-testid="products-profitability-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-indigo-500" />
              Rentabilite par produit
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={profitData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                  <XAxis type="number" stroke="#e2e8f0" tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(v) => `${v}`} />
                  <YAxis type="category" dataKey="name" stroke="#e2e8f0" tick={{ fill: '#334155', fontSize: 11 }} width={120} />
                  <Tooltip
                    contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }}
                    formatter={(value, name) => [
                      `${Number(value).toFixed(2)} CHF`,
                      name === 'purchases' ? 'Achats' : name === 'sales' ? 'Ventes' : 'Benefice'
                    ]}
                  />
                  <Bar dataKey="purchases" fill="#ef4444" radius={[0, 3, 3, 0]} name="purchases" barSize={12} />
                  <Bar dataKey="sales" fill="#10b981" radius={[0, 3, 3, 0]} name="sales" barSize={12} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex gap-6 mt-2 text-xs text-slate-500">
              <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-red-500" /> Achats</div>
              <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Ventes</div>
              {profitData[0] && profitData[0].profit > 0 && (
                <div className="flex items-center gap-1 text-emerald-600 font-medium ml-auto">
                  <ArrowUpRight className="w-3.5 h-3.5" />Top: {profitData[0].name}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Rechercher par nom ou SKU..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-products" />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]" data-testid="type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="product">Produits physiques</SelectItem>
                <SelectItem value="service">Services</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      {filtered.length > 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="products-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase">Produit</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase">Type</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase">SKU</th>
                    <th className="text-right p-4 text-xs font-semibold text-slate-500 uppercase">Prix achat</th>
                    <th className="text-right p-4 text-xs font-semibold text-slate-500 uppercase">Prix vente</th>
                    <th className="text-right p-4 text-xs font-semibold text-slate-500 uppercase">Marge</th>
                    <th className="text-center p-4 text-xs font-semibold text-slate-500 uppercase">Stock</th>
                    <th className="text-right p-4 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(product => {
                    const margin = product.unit_price - (product.purchase_price || 0);
                    const marginPercent = product.unit_price > 0 ? ((margin / product.unit_price) * 100).toFixed(0) : 0;
                    const isLowStock = product.product_type === 'product' && product.quantity_in_stock <= product.stock_alert_threshold;
                    return (
                      <tr key={product.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`product-row-${product.id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${product.product_type === 'product' ? 'bg-blue-50' : 'bg-purple-50'}`}>
                              {product.product_type === 'product' ? <Package className="w-4 h-4 text-blue-600" /> : <Wrench className="w-4 h-4 text-purple-600" />}
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5">
                                <p className="font-medium text-slate-900 text-sm">{product.name}</p>
                                {product.shopify_product_id && (
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-50 text-green-700 text-[10px] font-medium rounded" title="Lie a Shopify">
                                    <ShoppingBag className="w-2.5 h-2.5" />
                                  </span>
                                )}
                              </div>
                              {product.description && <p className="text-xs text-slate-500 truncate max-w-[200px]">{product.description}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${product.product_type === 'product' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                            {product.product_type === 'product' ? 'Produit' : 'Service'}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-slate-600 font-mono">{product.sku || '—'}</td>
                        <td className="p-4 text-sm text-slate-600 text-right">{product.purchase_price > 0 ? formatCurrency(product.purchase_price) : '—'}</td>
                        <td className="p-4 text-sm font-semibold text-slate-900 text-right">{formatCurrency(product.unit_price)}</td>
                        <td className="p-4 text-sm text-right">
                          {product.purchase_price > 0 ? (
                            <span className="text-green-600 font-medium">{formatCurrency(margin)} ({marginPercent}%)</span>
                          ) : '—'}
                        </td>
                        <td className="p-4 text-center">
                          {product.product_type === 'product' ? (
                            <div className="flex items-center justify-center gap-1">
                              <span className={`text-sm font-bold ${isLowStock ? 'text-amber-600' : 'text-slate-900'}`}>
                                {product.quantity_in_stock}
                              </span>
                              {isLowStock && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-1">
                            {product.product_type === 'product' && (
                              <>
                                <Button variant="ghost" size="sm" title="Entree/Sortie stock" onClick={() => { setMovementProduct(product); setMovementData({ type: 'in', quantity: 1, reason: '' }); setMovementDialogOpen(true); }} data-testid={`stock-move-${product.id}`}>
                                  <ArrowDownCircle className="w-4 h-4 text-emerald-500" />
                                </Button>
                                <Button variant="ghost" size="sm" title="Historique" onClick={() => openHistory(product)} data-testid={`stock-history-${product.id}`}>
                                  <History className="w-4 h-4 text-slate-500" />
                                </Button>
                              </>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(product)} data-testid={`edit-product-${product.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setProductToDelete(product.id); setDeleteDialogOpen(true); }} className="text-red-500 hover:text-red-700" data-testid={`delete-product-${product.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-1">Aucun produit</h3>
            <p className="text-slate-500 mb-4">Ajoutez vos produits et services</p>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />Ajouter un produit
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Modifier' : 'Nouveau produit / service'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Type</Label>
                <Select value={formData.product_type} onValueChange={(v) => setFormData({ ...formData, product_type: v })} data-testid="product-type-select">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product">Produit physique (avec stock)</SelectItem>
                    <SelectItem value="service">Service (sans stock)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Nom *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="product-name" />
              </div>
              {formData.product_type === 'product' && (
                <div>
                  <Label>SKU / Reference</Label>
                  <Input value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} placeholder="REF-001" data-testid="product-sku" />
                </div>
              )}
              <div>
                <Label>Unite</Label>
                <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unite">Unite</SelectItem>
                    <SelectItem value="heure">Heure</SelectItem>
                    <SelectItem value="jour">Jour</SelectItem>
                    <SelectItem value="forfait">Forfait</SelectItem>
                    <SelectItem value="kg">Kg</SelectItem>
                    <SelectItem value="m">Metre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prix d'achat (CHF)</Label>
                <Input type="number" step="0.01" min="0" value={formData.purchase_price} onChange={(e) => setFormData({ ...formData, purchase_price: parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} data-testid="product-purchase-price" />
              </div>
              <div>
                <Label>Prix de vente (CHF) *</Label>
                <Input type="number" step="0.01" min="0" value={formData.unit_price} onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} data-testid="product-sale-price" />
              </div>
              <div>
                <Label>TVA %</Label>
                <Input type="number" step="0.1" min="0" value={formData.vat_rate} onChange={(e) => setFormData({ ...formData, vat_rate: parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} />
              </div>
              {formData.product_type === 'product' && (
                <>
                  <div>
                    <Label>Quantite en stock</Label>
                    <Input type="number" min="0" value={formData.quantity_in_stock} onChange={(e) => setFormData({ ...formData, quantity_in_stock: parseInt(e.target.value) || 0 })} onFocus={(e) => e.target.select()} data-testid="product-stock" />
                  </div>
                  <div>
                    <Label>Seuil d'alerte</Label>
                    <Input type="number" min="0" value={formData.stock_alert_threshold} onChange={(e) => setFormData({ ...formData, stock_alert_threshold: parseInt(e.target.value) || 0 })} onFocus={(e) => e.target.select()} />
                  </div>
                </>
              )}
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
              </div>
            </div>
            {formData.purchase_price > 0 && formData.unit_price > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                <span className="text-green-700 font-medium">
                  Marge: {formatCurrency(formData.unit_price - formData.purchase_price)} ({((formData.unit_price - formData.purchase_price) / formData.unit_price * 100).toFixed(1)}%)
                </span>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-product-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{editingProduct ? 'Mettre a jour' : 'Creer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock Movement Dialog */}
      <Dialog open={movementDialogOpen} onOpenChange={setMovementDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Mouvement de stock — {movementProduct?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center text-sm text-slate-500">Stock actuel: <strong className="text-slate-900">{movementProduct?.quantity_in_stock}</strong></div>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant={movementData.type === 'in' ? 'default' : 'outline'} onClick={() => setMovementData({ ...movementData, type: 'in' })} className={movementData.type === 'in' ? 'bg-emerald-600 hover:bg-emerald-700' : ''} data-testid="movement-in">
                <ArrowDownCircle className="w-4 h-4 mr-2" />Entree
              </Button>
              <Button type="button" variant={movementData.type === 'out' ? 'default' : 'outline'} onClick={() => setMovementData({ ...movementData, type: 'out' })} className={movementData.type === 'out' ? 'bg-red-600 hover:bg-red-700' : ''} data-testid="movement-out">
                <ArrowUpCircle className="w-4 h-4 mr-2" />Sortie
              </Button>
            </div>
            <div>
              <Label>Quantite</Label>
              <Input type="number" min="1" value={movementData.quantity} onChange={(e) => setMovementData({ ...movementData, quantity: parseInt(e.target.value) || 0 })} onFocus={(e) => e.target.select()} data-testid="movement-qty" />
            </div>
            <div>
              <Label>Raison</Label>
              <Input value={movementData.reason} onChange={(e) => setMovementData({ ...movementData, reason: e.target.value })} placeholder="Livraison fournisseur, vente directe..." data-testid="movement-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovementDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleStockMovement} className={movementData.type === 'in' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'} data-testid="confirm-movement">
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Historique — {historyProduct?.name}</DialogTitle>
          </DialogHeader>
          {movements.length > 0 ? (
            <div className="max-h-[400px] overflow-y-auto space-y-2">
              {movements.map(m => (
                <div key={m.id} className={`flex items-center justify-between p-3 rounded-lg border ${m.type === 'in' ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
                  <div className="flex items-center gap-3">
                    {m.type === 'in' ? <ArrowDownCircle className="w-5 h-5 text-emerald-600" /> : <ArrowUpCircle className="w-5 h-5 text-red-600" />}
                    <div>
                      <p className="text-sm font-medium text-slate-900">{m.type === 'in' ? '+' : '-'}{m.quantity} unite(s)</p>
                      <p className="text-xs text-slate-500">{m.reason || 'Sans raison'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">{new Date(m.created_at).toLocaleDateString('fr-CH')}</p>
                    <p className="text-xs text-slate-400">{m.stock_before} → {m.stock_after}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">Aucun mouvement</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Shopify Import Dialog */}
      <Dialog open={shopifyDialogOpen} onOpenChange={setShopifyDialogOpen}>
        <DialogContent className="sm:max-w-[650px] max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-green-600" />
              Importer depuis Shopify
            </DialogTitle>
          </DialogHeader>
          {shopifyLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-green-500" />
              <span className="ml-3 text-slate-500">Chargement des produits Shopify...</span>
            </div>
          ) : shopifyProducts.length > 0 ? (
            <>
              <div className="text-sm text-slate-500 mb-2">
                {shopifyProducts.length} produit(s) trouves — {selectedShopifyIds.length} selectionne(s)
              </div>
              <div className="max-h-[400px] overflow-y-auto space-y-2 border rounded-lg p-2">
                {shopifyProducts.map(sp => (
                  <div
                    key={sp.shopify_id}
                    onClick={() => !sp.linked && toggleShopifySelect(sp.shopify_id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      sp.linked ? 'bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed' :
                      selectedShopifyIds.includes(sp.shopify_id) ? 'bg-green-50 border-green-300' :
                      'bg-white border-slate-200 hover:border-green-200'
                    }`}
                    data-testid={`shopify-product-${sp.shopify_id}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      sp.linked ? 'border-slate-300 bg-slate-200' :
                      selectedShopifyIds.includes(sp.shopify_id) ? 'border-green-500 bg-green-500' :
                      'border-slate-300'
                    }`}>
                      {(sp.linked || selectedShopifyIds.includes(sp.shopify_id)) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    {sp.image && <img src={sp.image} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900 truncate">{sp.title}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        {sp.sku && <span className="font-mono">{sp.sku}</span>}
                        {sp.vendor && <span>{sp.vendor}</span>}
                        <span>Stock: {sp.inventory_quantity}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-semibold text-sm">{sp.price.toFixed(2)} CHF</p>
                      {sp.linked && <span className="text-[10px] text-green-600 font-medium flex items-center gap-0.5"><Link2 className="w-3 h-3" />Lie</span>}
                    </div>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShopifyDialogOpen(false)}>Annuler</Button>
                <Button onClick={handleShopifyImport} disabled={shopifyImporting || selectedShopifyIds.length === 0} className="bg-green-600 hover:bg-green-700" data-testid="confirm-shopify-import">
                  {shopifyImporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShoppingBag className="w-4 h-4 mr-2" />}
                  Importer {selectedShopifyIds.length > 0 ? `(${selectedShopifyIds.length})` : ''}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="text-center py-12">
              <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucun produit trouve ou Shopify non connecte</p>
              <p className="text-xs text-slate-400 mt-1">Verifiez la connexion dans Parametres → Shopify</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce produit ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irreversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsPage;
